<template>
  <v-card elevation="0" data-testid="home-card-recognitions">
    <div
      class="d-flex align-center justify-space-between"
      data-testid="home-div-recognitionsHeader"
    >
      <span
        data-testid="home-text-recognitionsTitle"
        class="caption blue-grey--text text--darken-1 font-weight-medium"
      >
        {{ $t('home.activity_board.recognitions.title') }}
      </span>

      <Button
        v-if="hasRecognitions"
        color="primary"
        data-testid="home-button-viewAllRecognitions"
        plain
        size="small"
        :text="$t('home.activity_board.recognitions.view_all')"
        :to="{ name: 'recognitions' }"
        @click="goToRecognitions"
      />
    </div>

    <v-card
      v-if="isLoading"
      class="card-border my-3"
      outlined
      data-testid="home-card-recognitionsSkeleton"
    >
      <v-row class="py-2">
        <v-col v-for="item in 3" :key="`skeleton-${item}`" cols="12" md="12">
          <RecognitionItemSkeleton />
        </v-col>
      </v-row>
    </v-card>

    <v-card
      v-else
      class="card-border my-3"
      outlined
      data-testid="home-card-recognitionsContent"
    >
      <div
        v-if="!hasRecognitions"
        class="d-flex px-3 py-3"
        data-testid="home-div-recognitionsEmptyState"
      >
        <v-avatar size="32" color="yellow">
          <v-icon color="white">mdi-medal-outline</v-icon>
        </v-avatar>
        <div class="mx-3">
          <p class="body2 blue-grey--text text--darken-2 mb-1">
            {{ $t('recognitions.recognition_other') }}
          </p>
          <p class="caption blue-grey--text text--darken-1 mb-0">
            {{ $t('home.activity_board.recognitions.empty_state') }}
          </p>
        </div>
      </div>

      <v-row v-else no-gutters data-testid="home-row-recognitionsList">
        <v-col
          v-for="recognition in recognitionsList"
          :key="recognition._id"
          cols="12"
        >
          <RecognitionItem :recognition="recognition" />
        </v-col>
      </v-row>

      <div
        class="d-flex flex-column px-3 py-4"
        data-testid="home-div-recognitionsActions"
      >
        <Button
          color="primaryLighten"
          data-testid="home-button-giveRecognition"
          size="small"
          :text="$t('recognitions.recognize_someone')"
          @click="handleGiveRecognitionClick"
        />
      </div>
    </v-card>

    <RecognitionDialog
      v-if="showRecognitionDialog"
      :initialAccounts="[]"
      :show="showRecognitionDialog"
      data-testid="home-dialog-recognition"
      @close="showRecognitionDialog = false"
      @saved="handleSendRecognition"
    />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import Button from '@/components/UI/Button.vue'
import RecognitionDialog from '@/components/RecognitionDialog.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'

import RecognitionItem from './RecognitionItem.vue'
import RecognitionItemSkeleton from './ItemSkeleton.vue'

import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

export default {
  name: 'Recognitions',

  components: {
    Button,
    RecognitionItem,
    RecognitionItemSkeleton,
    RecognitionDialog,
  },

  mixins: [trackerMixin, mixin],

  data() {
    return {
      isLoading: true,
      isShowingCachedSnapshot: false,
      fetchTimer: null,
      recognitionsList: [],
      showRecognitionDialog: false,
    }
  },

  computed: {
    ...mapGetters(['accountId', 'organizationId']),

    hasRecognitions() {
      return !this.isLoading && !_.isEmpty(this.recognitionsList)
    },
  },

  mounted() {
    this.hydrateFromSnapshot()
    this.fetchTimer = setTimeout(
      this.fetchRecognitions,
      HOME_WIDGET_FETCH_DELAYS.activityBoard
    )
  },

  beforeDestroy() {
    clearTimeout(this.fetchTimer)
  },

  methods: {
    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot'](
        'activityBoard'
      )

      if (!_.isArray(snapshot?.recognitions)) return

      this.recognitionsList = [...snapshot.recognitions]
      this.isLoading = false
      this.isShowingCachedSnapshot = true
    },

    saveSnapshot() {
      this.$store.commit('homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        recognitions: [...this.recognitionsList],
      })
    },

    async fetchRecognitions() {
      try {
        if (!this.isShowingCachedSnapshot) this.isLoading = true

        const response = await api.recognition.getAll(this.organizationId, {
          limit: 3,
          sort: {
            by: 'createdAt',
            order: 'desc',
          },
        })

        this.recognitionsList = response?.data?.results ?? []
        this.isShowingCachedSnapshot = false

        this.saveSnapshot()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    goToRecognitions() {
      this.trackRecognitionsNavigate({
        option: 'goToRecognitionsScreen',
        section: 'home',
      })
    },

    handleSendRecognition() {
      this.fetchRecognitions()
      this.showRecognitionDialog = false
    },

    handleGiveRecognitionClick() {
      this.trackRecognitionsNavigate({
        option: 'giveRecognition',
        section: 'home',
      })

      this.showRecognitionDialog = true
    },
  },
}
</script>
